/* ── WORK_TRACK — Auth & profile ──────────────────────────── */

import { $, state, api, toast, showAuth, renderProfileMenu, openModal, closeModal, icon, setFieldError, clearFieldErrors } from './core';
import { closeChat } from './chat';
import { closeMeet } from './meet';
import { notifEnabled, setNotifEnabled, applyNotifIcon, loadNotifList } from './mentions';
import { setAuthScreen } from './auth-store';
import type { User } from './types';

export const urlParams = new URLSearchParams(window.location.search);
export const inviteFromUrl = urlParams.get('invite') || '';

export function setBootOwner(v: boolean): void {
  setAuthScreen({ bootOwner: !!v });
}

export function setRegister(on: boolean, opts?: { boot?: boolean; invite?: string }): void {
  setAuthScreen({
    mode: on ? 'register' : 'login',
    ...(opts && opts.boot ? { bootOwner: true } : {}),
    ...(opts && opts.invite ? { invite: opts.invite } : {})
  });
}

export function setResetToken(token: string): void {
  setAuthScreen({ resetToken: token });
}

export function doLogout(): void {
  closeChat();
  closeMeet();
  state.token = null;
  state.user = null;
  localStorage.removeItem('wt_token');
  localStorage.removeItem('wt_timer');
  setRegister(false);
  showAuth();
}

$('#profileLogoutBtn').addEventListener('click', doLogout);

// ── Profile menu: photo update / delete ──────────────────
const profileWrap = $('#profileWrap');
const profileMenu = $('#profileMenu');
const profileMenuMain = $('#profileMenuMain');
const profileMenuSettings = $('#profileMenuSettings');
const profileMenuNotifs = $('#profileMenuNotifs');
$('#profileBtn').addEventListener('click', e => {
  e.stopPropagation();
  profileMenu.classList.toggle('hidden');
  if (profileMenu.classList.contains('hidden')) return;
  profileMenuSettings.classList.add('hidden');
  profileMenuNotifs.classList.add('hidden');
  profileMenuMain.classList.remove('hidden');
  applyNotifIcon();
});
document.addEventListener('click', e => {
  if (profileWrap && !(e.target as HTMLElement).closest('#profileWrap')) profileMenu.classList.add('hidden');
});
$('#profileSettingsBtn').addEventListener('click', () => {
  profileMenuMain.classList.add('hidden');
  profileMenuNotifs.classList.add('hidden');
  profileMenuSettings.classList.remove('hidden');
});
$('#profileSettingsBack').addEventListener('click', () => {
  profileMenuSettings.classList.add('hidden');
  profileMenuMain.classList.remove('hidden');
});
$('#profileNotifsBtn').addEventListener('click', () => {
  profileMenuMain.classList.add('hidden');
  profileMenuSettings.classList.add('hidden');
  profileMenuNotifs.classList.remove('hidden');
  loadNotifList();
});
$('#profileNotifsBack').addEventListener('click', () => {
  profileMenuNotifs.classList.add('hidden');
  profileMenuMain.classList.remove('hidden');
});

// ── Settings: notifications ─────────────────────────────
const notifToggle = $('#profileNotifToggle');
const notifSwitch = $('#profileNotifSwitch');
const notifStatus = $('#profileNotifStatus');
function renderNotifToggle(): void {
  if (!notifToggle) return;
  const on = notifEnabled();
  if (notifSwitch) notifSwitch.classList.toggle('on', on);
  if (notifStatus) notifStatus.textContent = on ? 'On' : 'Off';
}
if (notifToggle) {
  notifToggle.addEventListener('click', () => {
    const on = !notifEnabled();
    setNotifEnabled(on);
    renderNotifToggle();
    applyNotifIcon();
    toast(on ? 'Notifications on' : 'Notifications off');
  });
  renderNotifToggle();
}

$<HTMLInputElement>('#profilePhotoInput').addEventListener('change', async e => {
  const input = e.target as HTMLInputElement;
  const file = input.files && input.files[0];
  if (!file) return;
  input.value = '';
  const maxSize = 8 * 1024 * 1024;
  if (file.size > maxSize) {
    toast('Photo must be under 8MB', true);
    return;
  }
  const btn = $<HTMLButtonElement>('#profileUpdateBtn');
  btn.disabled = true;
  btn.classList.add('loading');
  btn.innerHTML = `<span class="spinner"></span> Processing…`;
  try {
    const avatar = await compressAvatar(file);
    const data = await api<{ user: User }>('/api/auth/me', 'PUT', { avatar });
    state.user = data.user;
    renderProfileMenu();
    toast('Profile photo updated');
  } catch (err) {
    toast((err as Error).message || 'Could not process the image', true);
  } finally {
    btn.disabled = false;
    btn.classList.remove('loading');
    btn.innerHTML = `<svg class="ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> Update photo`;
  }
});

// Downscale + compress an image to a small square avatar (data URL).
function compressAvatar(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const size = 256;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;
        const s = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Not a valid image'));
    };
    img.src = url;
  });
}

$('#profileUpdateBtn').addEventListener('click', () => $<HTMLInputElement>('#profilePhotoInput').click());

$('#profileDeleteBtn').addEventListener('click', async () => {
  if (!confirm('Remove your profile photo?')) return;
  try {
    const data = await api<{ user: User }>('/api/auth/me', 'PUT', { avatar: null });
    state.user = data.user;
    renderProfileMenu();
    toast('Profile photo removed');
  } catch (err) {
    toast((err as Error).message, true);
  }
});

// ── Profile: change password ──────────────────────────────
$('#profileChangePwBtn').addEventListener('click', () => {
  const card = openModal(`
    <form class="modal-form" id="changePwForm">
      <div class="modal-head"><h2>Change password</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
      <div class="field"><label>Current password</label><input type="password" name="current_password" autocomplete="current-password" required></div>
      <div class="field"><label>New password</label><input type="password" name="new_password" autocomplete="new-password" required></div>
      <div class="field"><label>Confirm new password</label><input type="password" name="confirm" autocomplete="new-password" required></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" id="modalCancel">Cancel</button>
        <button type="submit" class="btn btn-primary">Update password</button>
      </div>
    </form>`);
  card.querySelector('.modal-close')?.addEventListener('click', closeModal);
  card.querySelector('#modalCancel')?.addEventListener('click', closeModal);
  card.querySelector<HTMLFormElement>('#changePwForm')!.addEventListener('submit', async e => {
    e.preventDefault();
    clearFieldErrors(card);
    const f = new FormData(card.querySelector<HTMLFormElement>('#changePwForm')!);
    const current = String(f.get('current_password') || '');
    const next = String(f.get('new_password') || '');
    const confirm = String(f.get('confirm') || '');
    if (!current) {
      setFieldError(card.querySelector<HTMLInputElement>('input[name="current_password"]')!, 'Enter your current password');
      return;
    }
    if (next.length < 8) {
      setFieldError(card.querySelector<HTMLInputElement>('input[name="new_password"]')!, 'Password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      setFieldError(card.querySelector<HTMLInputElement>('input[name="confirm"]')!, 'Passwords do not match');
      return;
    }
    const submitBtn = card.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    submitBtn.disabled = true;
    submitBtn.classList.add('loading');
    submitBtn.innerHTML = `<span class="spinner"></span> Saving…`;
    try {
      await api('/api/auth/change-password', 'POST', { current_password: current, new_password: next });
      closeModal();
      toast('Password updated');
    } catch (err) {
      toast((err as Error).message, true);
    } finally {
      submitBtn.disabled = false;
      submitBtn.classList.remove('loading');
      submitBtn.innerHTML = 'Update password';
    }
  });
});